import { useState } from 'react';
import type { CalculatorFormData, StepConfig } from '../calculatorTypes';
import { NumberInput } from '../shared/NumberInput';
import { getText } from '../calculatorUtils';
import type { CalcTexts } from '../locales';
import styles from '../Calculator.module.css';

interface Props {
  step: StepConfig;
  formData: CalculatorFormData;
  onNext: (data: Partial<CalculatorFormData>) => void;
  onBack: (data: Partial<CalculatorFormData>) => void;
  canGoBack: boolean;
  texts: CalcTexts;
}

export function MultiIncomeStep({ step, formData, onNext, onBack, canGoBack, texts }: Props) {
  const fields = step.fields!;
  const checkboxKey = step.checkboxKey as keyof CalculatorFormData;

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields.map(f => [f.key, (formData[f.key as keyof CalculatorFormData] as string) || ''])
    )
  );
  const [isChecked, setIsChecked] = useState<boolean>(
    (formData[checkboxKey] as boolean) ?? false
  );

  function buildData(coerceEmpty = false): Partial<CalculatorFormData> {
    const data: Partial<CalculatorFormData> = { [checkboxKey]: isChecked } as Partial<CalculatorFormData>;
    for (const field of fields) {
      (data as Record<string, unknown>)[field.key] = isChecked ? '0' : (coerceEmpty ? values[field.key] || '0' : values[field.key]);
    }
    return data;
  }

  function handleNext() {
    onNext(buildData(true));
  }

  return (
    <div className={styles.stepContent}>
      <p className={styles.stepQuestion}>{getText(texts, step.questionKey!)}</p>

      <div className={styles.multiIncomeList}>
        {fields.map(field => (
          <div key={field.key} className={styles.incomeRow}>
            <NumberInput
              id={field.key}
              value={values[field.key]}
              onChange={v => setValues(prev => ({ ...prev, [field.key]: v }))}
              label={getText(texts, field.labelKey)}
              disabled={isChecked}
              max={field.max}
            />
            <label htmlFor={field.key} className={styles.incomeLabel}>
              {getText(texts, field.labelKey)}
            </label>
          </div>
        ))}
      </div>

      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={e => setIsChecked(e.target.checked)}
          className={styles.checkbox}
        />
        <span>{getText(texts, step.checkboxLabelKey!)}</span>
      </label>

      <div className={styles.stepNav}>
        <button
          type="button"
          className={styles.btnBack}
          onClick={() => onBack(buildData())}
          disabled={!canGoBack}
        >
          {'< '}{texts.common.buttons.back}
        </button>
        <button type="button" className={styles.btnSecondary} onClick={handleNext}>
          {texts.common.buttons.continue}
        </button>
      </div>
    </div>
  );
}
